import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { size } from 'lodash';
import { useSelector } from 'react-redux';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { sendStudioShare, getEmailTemplates } from 'api/email';
import { styledTheme } from 'globalStyles/theme';
import APFormGroup from 'components/FormFields/APFormGroup';
import Clickwrap from 'components/Clickwrap';
import InputField from 'components/FormFields/InputField';
import TextArea from 'components/FormFields/TextArea';
import FormSelect from 'components/FormFields/FormSelect';
import Placeholder from 'assets/images/placeholder_file-lines.svg';
import styleGuideIcon from 'assets/images/notes/style-guide-empty.jpeg';
import { EmailSentConfirmation } from './EmailSentConfirmation';
import './styles.scss';

const dayjs = require('dayjs');

const EmailEditForm = ({
  projectId,
  notes,
  styleGuides,
  colorPalette,
  formData,
  onPreview,
  dismiss,
  showBcc = false,
  onPrev,
  showTemplates = false,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { orgId, personas, email: _email } = userInfo || {};
  const { isOrgOwner } = personas || {};
  const project = useSelector((state) => state.projectTools.project);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [ccEmail, setCCEmail] = useState('');
  const [bcc, setBCCList] = useState(null);
  const [bccSender, toggleBCCSender] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isFormValid, setFormValid] = useState(true);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [shareApiError, setApiError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [templates, setTemplates] = useState({});
  const [groupedTemplates, setGroupedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  // Clients shouldn't see templates.
  const [shouldShowTemplates, setShouldShowTemplates] = useState(true);

  const orgMemberName = `${userInfo.firstName} ${userInfo.lastName}`;
  const projectName = project?.name ?? '';
  const eventDate = project ?
    dayjs(project.weddingDate).format('MMM D, YYYY')
    : '';
  const userOrgId = useSelector((state) => state.global.userInfo.orgId);
  const projectOrgId = useSelector((state) => state.projectTools.project?.organizationId);
  const isClient = projectOrgId !== userOrgId;
  const isInTrial = project?.weddingInTrial;

  useEffect(() => {
    if (project) {
      setSubject(`${orgMemberName} has shared details for ${projectName} on ${eventDate}`);
    }
  }, [project]);

  useEffect(() => {
    const fetchEmailTemplates = async (_orgId) => {
      const [response] = await getEmailTemplates(_orgId);
      if (response.status === 200) {
        const templateObj = [];
        response.data.forEach(t => {
          const { id, name, message: _message } = t;
          templateObj[id] = {
            name,
            message: _message,
          };
          setTemplates(templateObj);
        });

        const groupedData = response.data.reduce((acc, item) => {
          const existingKind = acc.find(group => group.kind === item.kind);
          if (existingKind) {
            existingKind.templates.push(item);
          } else {
            acc.push({ kind: item.kind, templates: [item] });
          }
          return acc;
        }, []);

        setGroupedTemplates(groupedData);
      }
      setIsFormLoaded(true);
    };
    // Prevent spam from free trial accounts
    if (isClient || isInTrial) {
      setPrimaryEmail(`${_email},`);
    }
    if (orgId) {
      fetchEmailTemplates(orgId);
    } else if (project) {
      setShouldShowTemplates(false);
      setIsFormLoaded(true);
    }
  }, [orgId]);

  useEffect(() => {
    if (formData) {
      setPrimaryEmail(formData.primaryEmail);
      setSubject(formData.subject);
      setMessage(formData.message);
      toggleBCCSender(formData.bccSender || false);
      setBCCList(formData.bcc || null);
    }
  }, [formData]);

  const emailValid = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    if (templateId && templates[templateId]) {
      const { message: templateMsg } = templates[templateId];
      setSelectedTemplate(templateId);
      setMessage(templateMsg);
    }
    else {
      // #TODO: set error.
    }
  };

  // #TODO: This is rudimentary and can be improved. It can also be refactored into a util of some kind
  // or a separate email form field.
  // Validate emails are separated by commas.
  const emailStringValid = (emailStr) => {
    const split = emailStr.split(' ');
    // Handle extra spaces added in a comma delimited string.
    const emails = split.filter(str => str !== '');
    // If string contains spaces, check if each split has a comma at the end except for last.
    const len = emails.length;
    if (len > 1) {
      for (let i = 0; i < len; i += 1) {
        if (emails[i].charAt(emails[i].length - 1) !== ',' && i !== (len - 1)) {
          return false;
        }
        // Also check each individual email to see if valid.
        if (!emailValid(emails[i])) return false;
      }
    }
    return true;
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const isEmailFormValid = () => {
    if (!subject || !message) {
      setError('Please complete all required fields above.');
      return false;
    }
    if (!emailValid(primaryEmail) || !emailStringValid(primaryEmail)) {
      setError('One or more email addresses have a wrong format.');
      return false;
    }
    // #TODO: Add error message when we implement bcc field.
    if (!size(bcc)) return true;
    return !bcc.some(e => !e || !emailValid(e));
  };

  const onEmailSend = async () => {
    setApiError('');
    setFormSubmitted(true);
    if (isEmailFormValid()) {
      setError('');
      setErrorType('');
      setFormValid(true);
      const recievers = bcc || [];
      recievers.unshift(primaryEmail);
      const params = {
        'style_guides': styleGuides?.map(sg => sg.id) || [],
        'palettes': colorPalette?.map(cp => cp.id) || [],
        'notes': notes?.map(n => n.id) || [],
        'vendors': [],
        'recipients': bccSender && size(bcc) ? recievers.join(', ') : `${primaryEmail}`,
        'email_note': message,
        'captcha_response': null,
      };

      try {
        const response = await sendStudioShare(params, projectId);
        if (response && response.status === 200) {
          const { EMAIL_LIMIT_EXCEEDED } = response.data;

          if (EMAIL_LIMIT_EXCEEDED) {
            setApiError(`
              You’ve exceeded your daily email limit.
              To better support email deliverability, we’ve set a limit on
              the number of emails that you can send each day. Need to send
              more emails? We can help. Reach out to our support team through our
              in-app messenger towards the bottom right of your screen for assistance.
            `);
            return;
          }

          setIsSuccess(true);
        }
        else {
          const { errors, INVALID_MERGE_FIELD, INVALID_RECIPIENTS_TRIALER, error_fields } = response.data;
          const { recipients, email_note } = errors || {};
          if (recipients)
            setApiError('One or more email addresses have a wrong format.');
          else if (email_note && email_note[0] === 'Longer than maximum length 4000') {
            setApiError(`
              Your email exceeds the limit of 4000 characters.
              Please reduce the size of your message and try again.`,
            );
            setErrorType('message-too-long');
          }
          else if (INVALID_MERGE_FIELD) {
            const errorFields = error_fields.reduce((prev, curr) => `${prev}${prev === '' ? '' : ','} ${curr}`, '');
            setApiError(`
              We could not replace these merge fields: ${errorFields}.
              Please edit or remove these merge fields and try again.
            `);
            setErrorType('invalid-merge-fields');
          }
          else if (INVALID_RECIPIENTS_TRIALER) {
            if (!isOrgOwner || isClient) {
              setApiError(`
                We love how AP makes it easy to share your ideas and inspiration!
                To send emails to additional recipients, please reach out to your account owner to enable this feature.
              `);
            } else {
              setApiError(`
                We love how Aisle Planner makes it easy to share your ideas and inspiration. While you’ll only
                be able to send emails to yourself and one other person during your trial, you’ll have the ability
                to send to additional recipients once you’ve started a paid subscription.
              `);
            }
          }
          else
            setApiError('Something went wrong. Please try again.');
        }
      } catch(e) {
        setApiError(e);
      }
    } else {
      setFormValid(false);
    }
  };

  const onPreviewClick = () => {
    onPreview({
      subject,
      message,
      primaryEmail,
      bcc,
      bccSender,
    });
  };

  if (!isFormLoaded) {
    // #TODO: Add loading state.
    return null;
  }

  if (isSuccess) {
    return (
      <EmailSentConfirmation
        dismiss={() => {
          dismiss();
        }}
      />
    );
  }

  return (
    <section className="note_share_form__content">
      <APFormGroup className="address_group">
        <APFormGroup direction="vertical">
          <InputField
            name="primaryAddress"
            label="Recipients"
            sublabel="Separate multiple email addresses with commas."
            value={primaryEmail}
            onChange={(event) => setPrimaryEmail(event.target.value)}
            showError={isFormSubmitted && !emailValid(primaryEmail)}
            required
          />
          {showBcc && (
            <InputField
              name="ccAddress"
              label=""
              value={`CC: ${ccEmail}`}
              onChange={(event) => setCCEmail(event.target.value.substring(4))}
              showError={isFormSubmitted && !emailValid(ccEmail)}
            />
          )}
        </APFormGroup>
        {showBcc && (
          <span
            className="add_new_bcc__button"
            onClick={() => {
              let newList = [];
              if (size(bcc)) {
                newList = bcc.concat(['']);
              } else {
                newList = [''];
              }
              setBCCList(newList);
            }}
          >
            BCC +
          </span>
        )}
      </APFormGroup>
      {showBcc && bcc?.map((email, idx) => (
        <APFormGroup
          key={`bcc_${idx}`}
          className="bcc_input"
        >
          <InputField
            name={`bcc_${idx}`}
            label=""
            value={email}
            onChange={(e) => {
              const newBcc = bcc;
              newBcc[idx] = e.target.value;
              setBCCList([...newBcc]);
            }}
            showError={!!email && !emailValid(email)}
          />
          <span
            className="remove_bcc"
            onClick={() => {
              const newList = bcc;
              newList.splice(idx, 1);
              setBCCList([...newList]);
            }}
          >
            <HighlightOffIcon style={{ fontSize: '23px', color: styledTheme.errorColor }} />
          </span>
        </APFormGroup>
      ))}
      {showBcc && (
        <APFormGroup className="bcc-sender">
          <div className="bcc-sender__checkbox">
            <Clickwrap
              name="bcc-sender"
              checked={bccSender}
              onChange={() => toggleBCCSender(!bccSender)}
            />
            <label htmlFor="bcc-sender">
              BCC sender
            </label>
          </div>
        </APFormGroup>
      )}
      {(showTemplates && shouldShowTemplates) && (
        <APFormGroup className="template-group">
          <FormSelect
            name="Template"
            onChange={handleTemplateSelect}
            value={selectedTemplate}
          >
            {groupedTemplates.map((group, index) => (
              <optgroup
                key={`optgroup-${index}`}
                label={`${group.kind.replaceAll('-', ' ')} Email Templates`}
              >
                {group.templates.map(template => (
                  <option
                    key={`option-${template.id}`}
                    value={template.id}
                  >
                    {template.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </FormSelect>
        </APFormGroup>
      )}
      <APFormGroup className="subject-group">
        <InputField
          name="subject"
          label="Subject"
          value={subject}
          onChange={(event) => { setSubject(event.target.value); }}
          showError={isFormSubmitted && !subject}
          required
          disabled
        />
      </APFormGroup>
      <APFormGroup className="message__group">
        <TextArea // TODO: This is just a placeholder
          name="message"
          label="MESSAGE *"
          value={message}
          onChange={handleMessageChange}
          showError={isFormSubmitted && (!message || errorType === 'invalid-merge-fields')}
        />
      </APFormGroup>
      {size(notes) ? (
        <APFormGroup>
          <div className="note_info__group">
            <p>{size(notes) > 1 ? 'Notes' : 'Note'}:</p>
            {notes.map(note => {
              const { imageUrls } = note;
              const imageUrl = imageUrls?.rect120x80?.url;
              const noteTitle = note.title;
              return (
                <div
                  className="note_info"
                  key={note.id}
                >
                  <div className={clsx('email_form_note__image', !imageUrl && 'no-image')}>
                    <img
                      src={imageUrl ?? Placeholder}
                      alt="note background"
                    />
                  </div>
                  <span>{noteTitle}</span>
                </div>
              );
            })}
          </div>
        </APFormGroup>
      ): null}
      {size(colorPalette) ? (
        <APFormGroup>
          <div className="note_info__group">
            <p>{size(colorPalette) > 1 ? 'Color Palettes' : 'Color Palette'}:</p>
            {colorPalette.map(palette => {
              const paletteName = palette.name;
              const colors = palette.palette?.colors || [];
              const len = colors?.length;

              return (
                <div
                  className="note_info color-palette__info"
                  key={palette.id}
                >
                  <section className={clsx('color-palette-item__palette', !colors.len && 'no-colors')}>
                    {colors?.map(color => (
                      <div
                        key={color}
                        className="color-palette-item__preview"
                        style={{
                          width: `calc(100%/${len})`,
                          backgroundColor: `#${color}`,
                        }}
                      />
                    ))}
                  </section>
                  <span>{paletteName}</span>
                </div>
              );
            })}
          </div>
        </APFormGroup>
      ): null}
      {size(styleGuides) ? (
        <APFormGroup>
          <div className="note_info__group">
            <p>{size(styleGuides) > 1 ? 'Style Guides' : 'Style Guide'}:</p>
            {styleGuides.map(guide => {
              const heroImage = guide.images ? guide.images.find(img => img.isHeroImage) : null;
              const imageUrl = heroImage ? heroImage.imageUrls.square100?.url : null;
              const styleGuideName = guide.name;
              return (
                <div
                  className="note_info"
                  key={guide.id}
                >
                  <div className={clsx('email_form_styleguide__image', !imageUrl && 'no-image')}>
                    <img
                      src={imageUrl ?? styleGuideIcon}
                      alt="style guide background"
                    />
                  </div>
                  <span>{styleGuideName}</span>
                </div>
              );
            })}
          </div>
        </APFormGroup>
      ): null}
      <APFormGroup>
        <button
          className={clsx('button--unstyled', 'gold', 'email-preview')}
          onClick={onPreviewClick}
        >
          Preview
        </button>
      </APFormGroup>
      <APFormGroup>
        {!isFormValid && (
          <div className="validation__error">
            {error}
          </div>
        )}
        {shareApiError && (
          <div className="validation__error">
            {shareApiError}
          </div>
        )}
      </APFormGroup>
      <div className="share-form__buttons">
        {onPrev ? (
          <button
            type="button"
            className="button"
            onClick={onPrev}
          >
            Back
          </button>
        ) : null}
        <button
          type="button"
          className="button"
          onClick={onEmailSend}
        >
          Send
        </button>
      </div>
    </section>
  );
};

EmailEditForm.propTypes = {
  projectId: PropTypes.string,
  notes: PropTypes.array,
  styleGuides: PropTypes.array,
  colorPalette: PropTypes.array,
  formData: PropTypes.object,
  onPreview: PropTypes.func,
  dismiss: PropTypes.func,
  onPrev: PropTypes.func,
};

EmailEditForm.defaultProps = {};

export default EmailEditForm;
